import React, { useState } from 'react';
import {
  Menu,
  MenuList,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  useTheme,
} from '@mui/material';
import { tokens } from '../theme';
import { useSettings } from '../contexts/SettingsContext';
import { useSleeperConnection } from '../hooks/useSleeperConnection';

const SettingsMenu = ({ anchorEl, open, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { 
    scoringType, 
    platform, 
    teamCount,
    roundCount,
    isSuperflex,
    platformOptions,
    teamCountOptions,
    roundCountOptions,
    recentDraftIds,
    primaryLeagueId,
    handleScoringTypeChange, 
    handlePlatformChange,
    handleDraftIdChange,
    handleTeamCountChange,
    handleRoundCountChange,
    handleSuperflexChange,
    handlePrimaryLeagueChange,
    resetDraftboard,
    lastResetOptions,
  } = useSettings();

  const { leagues } = useSleeperConnection();

  const [resetDrafted, setResetDrafted] = useState(Boolean(lastResetOptions?.resetDrafted ?? true));
  const [resetKeepers, setResetKeepers] = useState(Boolean(lastResetOptions?.resetKeepers ?? false));

  // Format platform names with proper capitalization
  const formatPlatformName = (platformKey) => {
    const platformNames = {
      sleeper: 'Sleeper',
      yahoo: 'Yahoo',
      espn: 'ESPN',
      cbs: 'CBS',
      nfl: 'NFL'
    };
    return platformNames[platformKey] || platformKey;
  };

  const connectLiveDraft = () => {
    if (recentDraftIds.length > 0) {
      // Create a custom prompt with recent options
      const recentOptions = recentDraftIds.map((id, index) => `${index + 1}. ${id}`).join('\n');
      const message = `Recent Draft IDs:\n${recentOptions}\n\nEnter a number (1-${recentDraftIds.length}) to use a recent ID, or enter a new Draft ID:`;
      const input = prompt(message);
      
      if (input) {
        const numInput = parseInt(input);
        if (numInput >= 1 && numInput <= recentDraftIds.length) {
          // User selected a recent ID
          const selectedId = recentDraftIds[numInput - 1];
          handleDraftIdChange(selectedId);
        } else {
          // User entered a new ID
          handleDraftIdChange(input);
        }
      }
    } else {
      // No recent IDs, use simple prompt
      const id = prompt("Please enter or paste your Draft ID.");
      if (id) {
        handleDraftIdChange(id);
      }
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          minWidth: '300px',
          padding: '8px',
        }
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuList disablePadding>
        <Box sx={{ padding: '12px 16px' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: colors.grey[100], 
              marginBottom: '16px',
              fontWeight: 'bold'
            }}
          >
            Draft Settings
          </Typography>

          {/* Scoring Type */}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: colors.grey[100], 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Scoring Type
          </Typography>
          <RadioGroup
            value={scoringType}
            onChange={(e) => handleScoringTypeChange(e.target.value)}
            sx={{ marginBottom: '16px' }}
          >
            <FormControlLabel 
              value="standard" 
              control={
                <Radio 
                  size="small"
                  sx={{
                    color: colors.grey[400],
                    '&.Mui-checked': {
                      color: colors.greenAccent[500],
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.1rem',
                    },
                  }}
                />
              } 
              label={
                <Typography sx={{ color: colors.grey[100], fontSize: '14px' }}>
                  Standard
                </Typography>
              }
            />
            <FormControlLabel 
              value="ppr" 
              control={
                <Radio 
                  size="small"
                  sx={{
                    color: colors.grey[400],
                    '&.Mui-checked': {
                      color: colors.greenAccent[500],
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.1rem',
                    },
                  }}
                />
              } 
              label={
                <Typography sx={{ color: colors.grey[100], fontSize: '14px' }}>
                  PPR
                </Typography>
              }
            />
            <FormControlLabel 
              value="half_ppr" 
              control={
                <Radio 
                  size="small"
                  sx={{
                    color: colors.grey[400],
                    '&.Mui-checked': {
                      color: colors.greenAccent[500],
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.1rem',
                    },
                  }}
                />
              } 
              label={
                <Typography sx={{ color: colors.grey[100], fontSize: '14px' }}>
                  Half PPR
                </Typography>
              }
            />
          </RadioGroup>

          <Divider sx={{ backgroundColor: colors.grey[600], marginBottom: '16px' }} />

          {/* Roster Format */}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: colors.grey[100], 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Roster Format
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(isSuperflex)}
                  onChange={(e) => handleSuperflexChange(e.target.checked)}
                  sx={{
                    color: colors.grey[400],
                    '&.Mui-checked': { color: colors.greenAccent[500] },
                    '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                  }}
                />
              }
              label={
                <Typography sx={{ color: colors.grey[100], fontSize: '14px' }}>
                  Superflex (2QB)
                </Typography>
              }
            />
          </Box>

          <Divider sx={{ backgroundColor: colors.grey[600], marginBottom: '16px' }} />

          {/* Platform */}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: colors.grey[100], 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Platform
          </Typography>
          <FormControl fullWidth sx={{ marginBottom: '16px' }}>
            <Select
              value={platform}
              onChange={(e) => handlePlatformChange(e.target.value)}
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[600],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '& .MuiSvgIcon-root': {
                  color: colors.grey[100],
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.primary[400],
                    '& .MuiMenuItem-root': {
                      color: colors.grey[100],
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: colors.primary[600],
                      },
                      '&.Mui-selected': {
                        backgroundColor: colors.blueAccent[600],
                        '&:hover': {
                          backgroundColor: colors.blueAccent[700],
                        },
                      },
                    },
                  },
                },
              }}
            >
              {platformOptions.map((platformKey) => (
                <MenuItem key={platformKey} value={platformKey}>
                  {formatPlatformName(platformKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ backgroundColor: colors.grey[600], marginBottom: '16px' }} />

          {/* Primary League */}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: colors.grey[100], 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Primary League
          </Typography>
          <FormControl fullWidth sx={{ marginBottom: '16px' }}>
            <Select
              value={primaryLeagueId || ""}
              onChange={(e) => handlePrimaryLeagueChange(e.target.value)}
              size="small"
              displayEmpty
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[600],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '& .MuiSvgIcon-root': {
                  color: colors.grey[100],
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.primary[400],
                    '& .MuiMenuItem-root': {
                      color: colors.grey[100],
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: colors.primary[600],
                      },
                      '&.Mui-selected': {
                        backgroundColor: colors.blueAccent[600],
                        '&:hover': {
                          backgroundColor: colors.blueAccent[700],
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>Auto-select (last used → first available)</em>
              </MenuItem>
              {leagues.map((league) => (
                <MenuItem key={league.id} value={league.id}>
                  {league.name || `League ${league.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography 
            variant="caption"
            sx={{ 
              color: colors.grey[500], 
              fontSize: '12px',
              marginBottom: '16px',
              display: 'block'
            }}
          >
            Sets the default league for "All Keepers" when not in a specific league context
          </Typography>

          <Divider sx={{ backgroundColor: colors.grey[600], marginBottom: '16px' }} />

          {/* Team Count */}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: colors.grey[100], 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Number of Teams
          </Typography>
          <FormControl fullWidth sx={{ marginBottom: '16px' }}>
            <Select
              value={teamCount}
              onChange={(e) => handleTeamCountChange(e.target.value)}
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[600],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '& .MuiSvgIcon-root': {
                  color: colors.grey[100],
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.primary[400],
                    '& .MuiMenuItem-root': {
                      color: colors.grey[100],
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: colors.primary[600],
                      },
                      '&.Mui-selected': {
                        backgroundColor: colors.blueAccent[600],
                        '&:hover': {
                          backgroundColor: colors.blueAccent[700],
                        },
                      },
                    },
                  },
                },
              }}
            >
              {teamCountOptions.map((count) => (
                <MenuItem key={count} value={count}>
                  {count} Teams
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ backgroundColor: colors.grey[600], marginBottom: '16px' }} />

          {/* Round Count */}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: colors.grey[100], 
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Number of Rounds
          </Typography>
          <FormControl fullWidth sx={{ marginBottom: '16px' }}>
            <Select
              value={roundCount}
              onChange={(e) => handleRoundCountChange(e.target.value)}
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[600],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.blueAccent[500],
                },
                '& .MuiSvgIcon-root': {
                  color: colors.grey[100],
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: colors.primary[400],
                    '& .MuiMenuItem-root': {
                      color: colors.grey[100],
                      fontSize: '14px',
                      '&:hover': {
                        backgroundColor: colors.primary[600],
                      },
                      '&.Mui-selected': {
                        backgroundColor: colors.blueAccent[600],
                        '&:hover': {
                          backgroundColor: colors.blueAccent[700],
                        },
                      },
                    },
                  },
                },
              }}
            >
              {roundCountOptions.map((count) => (
                <MenuItem key={count} value={count}>
                  {count} Rounds
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ backgroundColor: colors.grey[600], marginBottom: '16px' }} />

          {/* Connect Draft Button */}
          <Button
            variant="contained"
            onClick={connectLiveDraft}
            fullWidth
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "13px",
              fontWeight: "bold",
              padding: "8px 16px",
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              }
            }}
          >
            Connect Draft
          </Button>

          {/* Reset Draftboard - inline panel with checkboxes */}
          <Box sx={{ mt: 1.5, p: 1.5, border: `1px solid ${colors.primary[600]}`, borderRadius: '8px', backgroundColor: colors.primary[500] }}>
            <Typography variant="subtitle2" sx={{ color: colors.grey[100], fontWeight: 800, mb: 1 }}>Reset Draftboard</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={resetDrafted} onChange={(e) => setResetDrafted(e.target.checked)} sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.greenAccent[500] } }} />}
                label={<Typography sx={{ color: colors.grey[100], fontSize: 14 }}>Reset drafted picks</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={resetKeepers} onChange={(e) => setResetKeepers(e.target.checked)} sx={{ color: colors.grey[400], '&.Mui-checked': { color: colors.redAccent[400] } }} />}
                label={<Typography sx={{ color: colors.grey[100], fontSize: 14 }}>Reset keepers / assigned positions</Typography>}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    resetDraftboard({ resetDrafted, resetKeepers });
                    onClose?.();
                  }}
                  sx={{
                    borderColor: colors.redAccent[500],
                    color: colors.redAccent[400],
                    fontWeight: 800,
                    flex: 1,
                  }}
                >
                  Confirm Reset
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </MenuList>
    </Menu>
  );
};

export default SettingsMenu; 